import api from '../api/client';
// Improved Receipt Preview Component
import React, { useState, useEffect, useRef } from 'react';
import { Form, InputNumber, Input, Select, Button, Upload, DatePicker, Divider, Space, Card, Table, Typography, Tag, Row, Col } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    UploadOutlined,
    PrinterOutlined,
    DownloadOutlined,
    ShopOutlined,
    PhoneOutlined,
    UserOutlined
} from '@ant-design/icons';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { Title, Text } = Typography;

// Receipt Preview Component (same as before)
export function ReceiptPreview({ data }) {
    if (!data || !data.items || data.items.length === 0) {
        return (
            <Card>
                <div style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
                    <PrinterOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <p>Receipt preview will appear here once form is filled</p>
                </div>
            </Card>
        );
    }

    const columns = [
        {
            title: 'Egg Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => getEggTypeLabel(type)
        },
        {
            title: 'Trays',
            dataIndex: 'trays',
            key: 'trays',
            align: 'center'
        },
        {
            title: 'Price',
            dataIndex: 'pricePerTray',
            key: 'pricePerTray',
            render: (price) => `KES ${price?.toLocaleString()}`,
            align: 'right'
        },
        {
            title: 'Subtotal',
            key: 'subtotal',
            render: (_, record) => `KES ${((record.trays || 0) * (record.pricePerTray || 0)).toLocaleString()}`,
            align: 'right'
        }
    ];

    const getStakeholderName = (id) => {
        return `Customer #${id}`;
    };

    const getEmployeeName = (id) => {
        return `Employee #${id}`;
    };

    const getEggTypeLabel = (type) => {
        const eggTypes = {
            normal: 'Normal Eggs',
            medium: 'Medium Eggs',
            double: 'Double Yolk',
            broken: 'Broken Eggs',
            spoilt: 'Spoilt (Discarded)'
        };
        return eggTypes[type] || type;
    };

    return (
        <Card
            title={
                <Space>
                    <PrinterOutlined />
                    Receipt Preview
                </Space>
            }
            style={{ marginTop: 16 }}
            bodyStyle={{ padding: 0 }}
        >
            <div style={{ padding: 24, background: 'white' }}>
                <div style={{ textAlign: 'center', marginBottom: 24, borderBottom: '2px solid #1890ff', paddingBottom: 16 }}>
                    <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                        EGG SALE RECEIPT
                    </Title>
                    <Text type="secondary">Farm Fresh Eggs</Text>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div>
                        <Text type="secondary">Customer: </Text>
                        <Text>{data.stakeholderId ? getStakeholderName(data.stakeholderId) : 'N/A'}</Text>
                    </div>
                    <div>
                        <Text type="secondary">Date: </Text>
                        <Text>{data.saleDate ? moment(data.saleDate).format('YYYY-MM-DD') : 'N/A'}</Text>
                    </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                    <Text type="secondary">Delivery Method: </Text>
                    <Text>{data.deliveredMethod === 'delivered' ? 'Delivered' : 'Picked at Farm'}</Text>

                    {data.deliveredMethod === 'delivered' && data.deliveryId && (
                        <>
                            <Text type="secondary"> • Delivery Person: </Text>
                            <Text>{getEmployeeName(data.deliveryId)}</Text>
                        </>
                    )}
                </div>

                <div style={{ marginBottom: 24 }}>
                    <Text type="secondary">Broken During Sales: </Text>
                    <Text>{data.brokenDuringSales || 0}</Text>
                </div>

                <Table
                    columns={columns}
                    dataSource={data.items.map((item, index) => ({ ...item, key: index }))}
                    pagination={false}
                    size="small"
                    summary={() => (
                        <Table.Summary>
                            <Table.Summary.Row style={{ background: '#fafafa' }}>
                                <Table.Summary.Cell index={0} colSpan={3}>
                                    <Text type="secondary">Total Amount</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <Text type="success" style={{ fontSize: '16px' }}>
                                        KES{' '}
                                        {data.totalAmount?.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                <div style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
                    <Text type="secondary">Payment Method: </Text>
                    <Text>{data.paymentMethod === 'cash' ? 'Cash' : 'On Credit'}</Text>
                </div>

                <div style={{ marginTop: 32, textAlign: 'center', borderTop: '1px solid #d9d9d9', paddingTop: 16 }}>
                    <Text type="secondary">Thank you for your business!</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        For inquiries, contact: +254 700 000 000
                    </Text>
                </div>
            </div>
        </Card>
    );
}

export function AnimalReceiptPreview({ data }) {
    const receiptRef = useRef();

    if (!data || !data.items || data.items.length === 0) {
        return (
            <Card>
                <div style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
                    <PrinterOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <p>Receipt preview will appear here once form is filled</p>
                </div>
            </Card>
        );
    }

    // Download as PDF function
    const downloadPdf = () => {
        const input = receiptRef.current;
        if (!input) return;

        html2canvas(input, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`animal-receipt-${data.receiptNumber || moment().format('YYYYMMDD-HHmmss')}.pdf`);
        });
    };

    // Print function
    const handlePrint = () => {
        const printContent = receiptRef.current.innerHTML;
        const originalContent = document.body.innerHTML;

        document.body.innerHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Receipt</title>
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box;
                        font-family: 'Inter', sans-serif;
                    }
                    body { 
                        padding: 20px; 
                        background: white;
                        color: #000;
                    }
                    .receipt-container {
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 25px;
                        border: 2px solid #000;
                        background: white;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 25px;
                        border-bottom: 3px solid #000;
                        padding-bottom: 20px;
                    }
                    .business-info {
                        background: #f8f9fa;
                        padding: 15px;
                        border-radius: 8px;
                        margin-bottom: 20px;
                        border-left: 4px solid #1890ff;
                    }
                    .customer-info {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 20px;
                        padding: 15px;
                        background: #f8f9fa;
                        border-radius: 8px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 20px 0;
                        font-size: 14px;
                    }
                    th {
                        background: #e9e9e9ff;
                        color: white;
                        padding: 12px 8px;
                        text-align: left;
                        border: 1px solid #e9e9e9ff;
                    }
                    td {
                        padding: 10px 8px;
                        border: 1px solid #e9e9e9ff;
                    }
                    tr:nth-child(even) {
                        background: #ecf0f1;
                    }
                    .total-section {
                        background: #2c3e50;
                        color: white;
                        padding: 15px;
                        text-align: right;
                        font-weight: bold;
                        font-size: 18px;
                        margin-top: 20px;
                    }
                    .footer {
                        text-align: center;
                        margin-top: 30px;
                        padding-top: 20px;
                        border-top: 2px dashed #bdc3c7;
                        color: #7f8c8d;
                    }
                    .payment-method {
                        background: #e8f5e8;
                        padding: 12px;
                        border-radius: 6px;
                        margin: 15px 0;
                        border-left: 4px solid #27ae60;
                    }
                    @media print {
                        @page {
                            margin: 0.5in;
                            size: auto;
                        }
                        body {
                            padding: 0;
                        }
                        .no-print {
                            display: none !important;
                        }
                    }
                </style>
            </head>
            <body>
                ${printContent}
            </body>
            </html>
        `;

        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload();
    };

    // Helper functions
    const getStakeholderName = (id) => {
        return `Customer #${id}`;
    };

    const getEmployeeName = (id) => {
        return `Employee #${id}`;
    };

    const getSpeciesLabel = (species) => {
        const speciesLabels = {
            goats: 'Goats',
            pigs: 'Pigs',
            poultry: 'Poultry',
            sheep: 'Sheep',
            weaners: 'Weaners'
        };
        return speciesLabels[species] || species;
    };

    const getCategoryLabel = (category) => {
        return category
            ? category
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
            : 'N/A';
    };

    // Table columns
    const columns = [
        {
            title: 'Species',
            dataIndex: 'species',
            key: 'species',
            render: (species) => (
                <Tag color="blue" style={{ fontWeight: 600 }}>
                    {getSpeciesLabel(species)}
                </Tag>
                // <Text type="secondary" >{species}</Text>
            ),
            width: '15%'
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => getCategoryLabel(category),
            width: '20%'
        },
        {
            title: 'Identifier',
            dataIndex: 'identifier',
            key: 'identifier',
            render: (identifier, record) => {
                if (record.species === 'poultry') return `Batch: ${identifier}`;
                if (record.species === 'weaners') return `Tag: ${identifier}`;
                return `Tag: ${identifier}`;
            },
            width: '20%'
        },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
            width: '10%',
            render: (quantity) => <Text type="secondary">{quantity}</Text>
        },
        {
            title: 'KGs',
            dataIndex: 'weight',
            key: 'weight',
            align: 'center',
            width: '10%',
            render: (weight) => <Text type="secondary">{weight}</Text>
        },
        {
            title: 'Price',
            dataIndex: 'pricePerAnimal',
            key: 'pricePerAnimal',
            align: 'right',
            width: '15%',
            render: (price) => `${price?.toLocaleString()}`
        },
        {
            title: 'Subtotal',
            key: 'subtotal',
            align: 'right',
            width: '20%',
            render: (_, record) => <Text type="success">{((record.weight || 0) * (record.pricePerAnimal || 0)).toLocaleString()}</Text>
        }
    ];

    return (
        <>
            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
                
                .receipt-container {
                    font-family: 'Inter', sans-serif;
                }
                
                @media print {
                    @page {
                        margin: 0.5in;
                        size: auto;
                    }
                    body * {
                        visibility: hidden;
                    }
                    .printable-receipt, .printable-receipt * {
                        visibility: visible;
                    }
                    .printable-receipt {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
                
                .ant-table-thead > tr > th {
                    background: #f8f9fa !important;
                    color: black !important;
                    font-weight: 400;
                }
                
                .receipt-table .ant-table {
                    font-size: 12px;
                }
                
                @media (max-width: 768px) {
                    .receipt-table .ant-table {
                        font-size: 11px;
                    }
                    .receipt-table .ant-table-thead > tr > th,
                    .receipt-table .ant-table-tbody > tr > td {
                        padding: 8px 4px;
                    }
                }
                `}
            </style>

            {/* <Card
                title={
                    <Space>
                        <PrinterOutlined />
                        Animal Sale Receipt
                    </Space>
                }
                style={{ marginTop: 16 }}
                extra={
                    <Space className="no-print">
                        <Button icon={<PrinterOutlined />} onClick={handlePrint} size="middle">
                            Print Receipt
                        </Button>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={downloadPdf} size="middle">
                            Save as PDF
                        </Button>
                    </Space>
                }
                bodyStyle={{ padding: 0 }}
            > */}
            <div
                ref={receiptRef}
                className="printable-receipt receipt-container"
                style={{
                    padding: '20px',
                    background: 'white',
                    minHeight: '297mm',
                    maxWidth: '800px',
                    margin: '0 auto'
                }}
            >
                {/* Header Section */}
                {/* <div
                    className="header"
                    style={{ textAlign: 'center', marginBottom: 30, borderBottom: '3px solid #1890ff', paddingBottom: 25 }}
                >
                    <Title level={1} style={{ margin: 0, color: '#1890ff', fontSize: '32px', fontWeight: 700 }}>
                        ANIMAL SALE RECEIPT
                    </Title>
                    <Text style={{ fontSize: '16px', color: '#666', display: 'block', marginTop: 8 }}>
                        Farm Livestock Sales & Distribution
                    </Text>
                </div> */}

                {/* Business Info */}
                <div className="business-info" style={{ background: '#f8f9fa', padding: 20, borderRadius: 10, marginBottom: 25 }}>
                    <Row gutter={16} align="middle">
                        <Col>
                            <ShopOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                        </Col>
                        <Col flex="1">
                            <Text strong style={{ fontSize: '16px' }}>
                                Green Valley Farm Enterprises
                            </Text>
                            <br />
                            <Text type="secondary">P.O Box 1234-00100, Nairobi, Kenya</Text>
                            <br />
                            <Text type="secondary">
                                <PhoneOutlined /> +254 700 000 000 | Email: info@greenvalleyfarm.co.ke
                            </Text>
                        </Col>
                        {/* <Col>
                                <Text type='secondary'>Receipt #: {data.receiptNumber || `RCP-${moment().format('YYYYMMDD-HHmmss')}`}</Text>
                            </Col> */}
                    </Row>
                </div>

                {/* Customer and Sale Info */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col xs={24} md={12}>
                        {/* <Card size="small" title="Customer Information" style={{ height: '100%' }}> */}
                        <Space direction="vertical">
                            <div>
                                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                <Text type="secondary">Customer: </Text>
                                <Text>{data.stakeholderId ? getStakeholderName(data.stakeholderId) : 'N/A'}</Text>
                            </div>
                            <div>
                                <Text type="secondary">Contact: </Text>
                                <Text>+254 XXX XXX XXX</Text>
                            </div>
                        </Space>
                        {/* </Card> */}
                    </Col>
                    <Col xs={24} md={12}>
                        {/* <Card size="small" title="Sale Information" style={{ height: '100%' }}> */}
                        <Space direction="vertical">
                            <div>
                                <Text type="secondary">Sale Date: </Text>
                                <Text>{data.saleDate ? moment(data.saleDate).format('DD/MM/YYYY') : 'N/A'}</Text>
                            </div>
                            <div>
                                <Text type="secondary">Delivery: </Text>
                                <Tag color={data.deliveredMethod === 'delivered' ? 'green' : 'orange'}>
                                    {data.deliveredMethod === 'delivered' ? 'Delivered' : 'Picked at Farm'}
                                </Tag>
                                {data.deliveredMethod === 'delivered' && data.deliveryPersonId && (
                                    <Text> by {getEmployeeName(data.deliveryPersonId)}</Text>
                                )}
                            </div>
                        </Space>
                        {/* </Card> */}
                    </Col>
                </Row>

                {/* Items Table */}
                <div className="receipt-table" style={{ marginBottom: 25 }}>
                    <Title level={5} style={{ marginBottom: 16 }}>
                        Items Sold
                    </Title>
                    <Table
                        columns={columns}
                        dataSource={data.items.map((item, index) => ({ ...item, key: index }))}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                        summary={() => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={6}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            TOTAL AMOUNT
                                        </Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        <Text type="secondary" style={{ fontSize: '14px' }}>
                                            {/* KES{' '} */}
                                            {data.totalAmount?.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                </div>

                {/* Payment Method */}
                {data.paymentMethod && (
                    <div
                        className="payment-method"
                        style={{
                            background: '#e8f5e8',
                            padding: 16,
                            borderRadius: 8,
                            marginBottom: 20,
                            borderLeft: '4px solid #52c41a'
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '15px' }}>
                            Payment Method:{' '}
                        </Text>
                        <Tag color={data.paymentMethod === 'cash' ? 'green' : 'orange'} style={{ fontSize: '14px', padding: '4px 8px' }}>
                            {data.paymentMethod === 'cash' ? 'CASH PAYMENT' : 'ON CREDIT'}
                        </Tag>
                        {data.paymentMethod === 'unpaid' && (
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                (Payment due upon delivery)
                            </Text>
                        )}
                    </div>
                )}

                {/* Terms and Footer */}
                <Divider />
                <div className="footer" style={{ textAlign: 'center', marginTop: 30, paddingTop: 20, borderTop: '2px dashed #d9d9d9' }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        Thank you for your business! We appreciate your trust in our livestock.
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        Terms: All sales are final. Animals are guaranteed healthy at time of sale. Please inspect your animals upon
                        receipt.
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 8 }}>
                        For inquiries: +254 700 000 000 | Email: support@greenvalleyfarm.co.ke
                    </Text>
                </div>

                {/* Signature Section */}
                <Row gutter={16} style={{ marginTop: 40 }}>
                    <Col span={12}>
                        <div style={{ textAlign: 'center', paddingTop: 20 }}>
                            <Divider style={{ margin: '10px 0' }} />
                            <Text type="secondary">Customer Signature</Text>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ textAlign: 'center', paddingTop: 20 }}>
                            <Divider style={{ margin: '10px 0' }} />
                            <Text type="secondary">Authorized Farm Representative</Text>
                        </div>
                    </Col>
                </Row>
            </div>
            {/* </Card> */}
        </>
    );
}
